/**
 * Query API over the transition table (transitions.js). The FlowEngine builds a
 * context for the current user + row (via the entity adapter) and asks this module
 * "what can I do from here".
 *
 * Context shape (produced by the adapter, USER-RELATIVE):
 *   ctx = {
 *     actorKeys: string[],   // which contextual actors this user is: always ['either'],
 *                            // plus 'recipient' if they own the request, 'accused' if
 *                            // they are the no_show_party.
 *     userId: string,
 *     row: {                 // abstract row the guards read, relative to this user:
 *       proposedBy, iProposed, iArrived, otherArrived,
 *       iConfirmed, otherConfirmed, travelDate, disputedAt
 *     }
 *   }
 * 'system' transitions are never in a user's actorKeys, so they're never offered client-side.
 * Arrival/handoff guards read the COUNTERPART's state ("other already acted") because the
 * acting user's own mark/confirm is applied by the same advance_flow call.
 */
import { TRANSITIONS, TERMINAL_STATES, CARD_FOR } from './transitions';

const APPEAL_WINDOW_MS = 10 * 60 * 1000;

// IMPORTANT: the anti-abuse guards below are ALSO enforced server-side inside the
// `advance_flow` RPC (migration `advance_flow_server_side_guard_enforcement`), so a
// crafted raw RPC can't bypass them. If you add/rename/retune a guard here, update
// advance_flow's guard block to match — otherwise a legitimate client action can be
// rejected by the server (or a bypass slips through). Enforced server-side:
// not_proposer, not_i_arrived, i_arrived, other_confirmed_presence,
// not_other_confirmed_presence, other_confirmed, not_late_cancel/is_late_cancel,
// plus the actor-role checks (accused → admit/dispute_no_show, recipient →
// request/decline). mark_done's travel_date_passed is intentionally client-only.

export const guards = {
  // The party agreeing to a time must not be the one who proposed it.
  not_proposer: (row) => row.proposedBy != null && row.iProposed === false,
  // The acting user has marked present (can report the other as a no-show).
  i_arrived: (row) => row.iArrived === true,
  // The acting user has NOT marked present yet — so a second mark_here in `waiting`
  // is only offered to the party who still needs to arrive (not the one waiting).
  not_i_arrived: (row) => row.iArrived !== true,
  // The other party has already marked present (this user's mark makes it both).
  other_arrived: (row) => row.otherArrived === true,
  // The other party has already confirmed the current user is present (this user's
  // confirm completes both_arrived -> both_present); the negation self-loops.
  other_confirmed_presence: (row) => row.otherPresenceConfirmed === true,
  not_other_confirmed_presence: (row) => row.otherPresenceConfirmed !== true,
  // The other party has already confirmed handoff (this user's confirm completes it).
  other_confirmed: (row) => row.otherConfirmed === true,
  travel_date_passed: (row) =>
    row.travelDate != null && new Date(row.travelDate).getTime() < Date.now(),
  // You're the absent party (haven't arrived) bailing while the OTHER is already
  // waiting and the meeting time has passed — cancelling here is your no-show.
  is_late_cancel: (row) =>
    row.otherArrived === true &&
    row.iArrived !== true &&
    row.meetingTime != null &&
    new Date(row.meetingTime).getTime() <= Date.now(),
  not_late_cancel: (row) =>
    !(row.otherArrived === true &&
      row.iArrived !== true &&
      row.meetingTime != null &&
      new Date(row.meetingTime).getTime() <= Date.now()),
  appeal_window_expired: (row) =>
    row.disputedAt != null && Date.now() - new Date(row.disputedAt).getTime() > APPEAL_WINDOW_MS,
};

export function isTerminal(status) {
  return TERMINAL_STATES.includes(status);
}

export function cardFor(status) {
  return CARD_FOR[status];
}

/**
 * Actions available from `status` for the user described by `ctx`.
 * Returns [{ action, to, guardOk }]. guardOk=false means the transition exists for
 * this user but its guard isn't satisfied yet (render disabled/explained).
 */
export function actionsFor(status, ctx = {}) {
  const actorKeys = ctx.actorKeys || ['either'];
  const row = ctx.row || {};
  return TRANSITIONS.filter((t) => t.from === status)
    .filter((t) => actorKeys.includes(t.roles))
    .map((t) => ({
      action: t.action,
      to: t.to,
      guardOk: t.guard == null ? true : guards[t.guard] ? guards[t.guard](row, ctx) : false,
    }));
}
